import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useResizeDetector } from "react-resize-detector";
import { Link } from "react-router-dom";
import AlertModal from "common/components/alertDialog";
import { useOpenGlobalDuplicatePostDialog } from "common/components/dialogs/globalDuplicatePostDialog/hooks";
import { useOpenGlobalMovePostDialog } from "common/components/dialogs/globalMovePostDialog/hooks";
import NoRightAlert, {
  IRefHandler as INoRightRefHandler,
} from "common/components/feedBack/components/noRight/alert";
import { useOpenPostReportDialog } from "common/components/reportDialog/presets/post/hooks";
import ResponsiveMenu from "common/components/responsiveMenu";
import { MenuWrapper } from "common/components/responsiveMenu/components/menu";
import useThreadMenuPermissions from "common/components/thread/templates/common/hooks/useThreadMenuPermissions";
import { MoimURL } from "common/helpers/url";
import useIsMobile from "common/hooks/useIsMobile";
import { MenuItem } from "./styled";

interface IProps {
  open: boolean;
  thread: Moim.Forum.IThread;
  refMenuButton: React.RefObject<HTMLDivElement>;
  onClose(): void;
  onDeleteThread(): void;
}

const ThreadItemMenu = ({
  thread,
  open,
  refMenuButton,
  onClose,
  onDeleteThread,
}: IProps) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {
    hasEditPermission,
    hasDeletePermission,
    visibleReportButton,
    hasDuplicatePostPermission,
    hasMovePostPermission,
  } = useThreadMenuPermissions(thread);

  const refNoRightAlert = React.useRef<INoRightRefHandler>(null);
  const [minHeight, setMinHeight] = React.useState<number | undefined>(0);
  const [isOpenDeleteConfirm, setDeleteConfirmOpenStatus] =
    React.useState(false);

  const editThreadUrlObject = React.useMemo(() => {
    if (isMobile) {
      return new MoimURL.EditForumThread({
        threadId: thread.id,
        forumId: thread.parent_id,
      }).toString();
    }
    const { pathname, search } = new MoimURL.BlockitEditor().toObject();
    const qs = new URLSearchParams(search);
    if (thread.parent_id) {
      qs.set("channel", thread.parent_id);
    }
    if (thread.id) {
      qs.set("thread", thread.id);
    }

    return {
      pathname,
      search: qs.toString(),
    };
  }, [isMobile, thread.parent_id, thread.id]);

  const handleResponsiveMenuWrapperResize = React.useCallback(
    (_width: number, height: number) => {
      setMinHeight(height);
    },
    [],
  );

  const { ref: refMenuWrapper } = useResizeDetector({
    handleHeight: true,
    onResize: handleResponsiveMenuWrapperResize,
  });

  const handleOpenDeleteConfirm = React.useCallback(() => {
    setDeleteConfirmOpenStatus(true);
  }, []);
  const handleCloseDeleteConfirm = React.useCallback(() => {
    setDeleteConfirmOpenStatus(false);
  }, []);

  const handleDeleteThread = React.useCallback(() => {
    if (hasDeletePermission) {
      handleOpenDeleteConfirm();
    } else {
      refNoRightAlert.current?.openHandler();
    }
  }, [hasDeletePermission, handleOpenDeleteConfirm]);

  const openPostReportDialog = useOpenPostReportDialog({
    parentId: thread.author,
    threadId: thread.id,
  });
  const openGlobalDuplicatePostDialog = useOpenGlobalDuplicatePostDialog({
    threadId: thread.id,
  });
  const openGlobalMovePostDialog = useOpenGlobalMovePostDialog({
    threadId: thread.id,
  });

  return (
    <>
      <ResponsiveMenu
        open={open}
        anchorElement={refMenuButton.current}
        onCloseRequest={onClose}
        minHeight={minHeight}
      >
        <MenuWrapper ref={refMenuWrapper}>
          {hasEditPermission && (
            <MenuItem key="mene_edit_thread">
              <Link to={editThreadUrlObject}>
                <FormattedMessage id="edit_button" />
              </Link>
            </MenuItem>
          )}

          {hasDeletePermission && (
            <MenuItem key="mene_delete_thread" onClick={handleDeleteThread}>
              <FormattedMessage id="delete_button" />
            </MenuItem>
          )}

          {visibleReportButton && (
            <MenuItem
              key="menu_report"
              onClick={() => {
                openPostReportDialog();
                onClose();
              }}
            >
              <FormattedMessage id="more_menu_report" />
            </MenuItem>
          )}
          {hasDuplicatePostPermission && (
            <MenuItem
              key="menu_copy"
              onClick={() => {
                openGlobalDuplicatePostDialog();
                onClose();
              }}
            >
              <FormattedMessage id="more_menu_copy" />
            </MenuItem>
          )}
          {hasMovePostPermission && (
            <MenuItem
              key="menu_move"
              onClick={() => {
                openGlobalMovePostDialog();
                onClose();
              }}
            >
              <FormattedMessage id="more_menu_move" />
            </MenuItem>
          )}
        </MenuWrapper>
      </ResponsiveMenu>
      <AlertModal
        open={isOpenDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        content={
          <FormattedMessage id="post_show/post_more_menu_delete_dialog_body" />
        }
        rightButtons={[
          {
            text: intl.formatMessage({ id: "cancel_button" }),
            onClick: handleCloseDeleteConfirm,
          },
          {
            text: intl.formatMessage({ id: "delete_button" }),
            onClick: onDeleteThread,
          },
        ]}
      />
      <NoRightAlert ref={refNoRightAlert} />
    </>
  );
};

export default ThreadItemMenu;
